/** @file groundMarks: the faint body-facing chevron and the soft shadow blob on the ground, built once. */
import { Constants } from '@babylonjs/core/Engines/constants.js';
import { ShaderMaterial } from '@babylonjs/core/Materials/shaderMaterial.js';
import { Quaternion } from '@babylonjs/core/Maths/math.vector.js';
import { Mesh } from '@babylonjs/core/Meshes/mesh.js';
import { VertexData } from '@babylonjs/core/Meshes/mesh.vertexData.js';
import type { Scene } from '@babylonjs/core/scene.js';
import { ringTurn } from './ringTurn';
import { VIEWPORT_LAYERS } from './viewportLayers';

/** Body facing (pose yaw, 0 = south, clockwise from above), where the figure stands (world x, y) and its half extents. */
export type GroundInput = { facingDeg: number; at: readonly [number, number]; half: readonly [number, number] };

/** `update(input)` once per frame (null hides both); `meshes` for tests. */
export type GroundMarks = { chevron: Mesh; shadow: Mesh; update(input: GroundInput | null): void };

/** 20 % alpha (task T-315); the shadow fades from it at the centre to nothing at the rim. */
const ALPHA = 0.2;
const SEGMENTS = 32;
/** The chevron in units for a figure half a unit wide: tip, back corners and the arm thickness, pointing south. */
const CHEVRON = { tip: 0.8, back: 0.48, side: 0.3, thick: 0.1 } as const;

const VERTEX = 'precision highp float; attribute vec3 position; attribute vec4 color; uniform mat4 worldViewProjection; varying vec4 vColor; void main() { vColor = color; gl_Position = worldViewProjection * vec4(position, 1.0); }';
const FRAGMENT = 'precision highp float; varying vec4 vColor; void main() { gl_FragColor = vColor; }';

/** The vertex-colour material both marks share: blended, both faces, no depth test or write (they lie on the grid). */
function groundMaterial(scene: Scene): ShaderMaterial {
  const m = new ShaderMaterial('viewport-ground-material', scene, { vertexSource: VERTEX, fragmentSource: FRAGMENT }, {
    attributes: ['position', 'color'], uniforms: ['worldViewProjection'], needAlphaBlending: true,
  });
  Object.assign(m, { backFaceCulling: false, disableDepthWrite: true, depthFunction: Constants.ALWAYS, alphaMode: Constants.ALPHA_COMBINE });
  return m;
}

/** A static mesh from positions (Babylon x, y, z), RGBA colours and triangles, on the overlay layer in group 0. */
function staticMesh(name: string, scene: Scene, material: ShaderMaterial, data: { positions: number[]; colors: number[]; indices: number[] }): Mesh {
  const mesh = new Mesh(name, scene);
  const v = new VertexData();
  Object.assign(v, data);
  v.applyToMesh(mesh, false);
  Object.assign(mesh, { material, layerMask: VIEWPORT_LAYERS.overlay, renderingGroupId: 0, isPickable: false, alwaysSelectAsActiveMesh: true });
  mesh.rotationQuaternion = new Quaternion();
  mesh.setEnabled(false);
  return mesh;
}

/** A unit disc on the ground: a dark centre at `ALPHA` fading out through a half-radius ring to a clear rim. */
function shadowData(): { positions: number[]; colors: number[]; indices: number[] } {
  const positions = [0, 0, 0];
  const colors = [0, 0, 0, ALPHA];
  for (const [r, a] of [[0.55, ALPHA * 0.75], [1, 0]] as const) {
    for (let i = 0; i < SEGMENTS; i++) {
      const t = (2 * Math.PI * i) / SEGMENTS;
      positions.push(r * Math.cos(t), 0, r * Math.sin(t));
      colors.push(0, 0, 0, a);
    }
  }
  const indices: number[] = [];
  for (let i = 0; i < SEGMENTS; i++) {
    const [a, b] = [1 + i, 1 + ((i + 1) % SEGMENTS)];
    indices.push(0, a, b, a, a + SEGMENTS, b + SEGMENTS, a, b + SEGMENTS, b);
  }
  return { positions, colors, indices };
}

/** A filled `^` in front of the origin pointing south (Babylon -z, world -y): two arms of `thick` width. */
function chevronData(): { positions: number[]; colors: number[]; indices: number[] } {
  const { tip, back, side, thick } = CHEVRON;
  const pts = [[-side, -back], [0, -tip], [side, -back], [side, -back + thick], [0, -tip + thick], [-side, -back + thick]];
  return {
    positions: pts.flatMap(([x = 0, z = 0]) => [x, 0, z]),
    colors: pts.flatMap(() => [0.85, 0.87, 0.91, ALPHA]),
    indices: [0, 1, 4, 0, 4, 5, 1, 2, 3, 1, 3, 4],
  };
}

/**
 * The shadow (an ellipse of the figure's half extents under it) and the chevron (at the entity origin, turned to the
 * body facing: a right-handed turn of `-facing` about up maps south to the facing direction), both GPU meshes on the
 * overlay layer in the grid's group (drawn at full resolution under the puppet), built once and moved in place.
 */
export function groundMarks(scene: Scene): GroundMarks {
  const material = groundMaterial(scene);
  const shadow = staticMesh('viewport-ground-shadow', scene, material, shadowData());
  const chevron = staticMesh('viewport-ground-chevron', scene, material, chevronData());
  return {
    chevron, shadow,
    update: (g) => {
      shadow.setEnabled(g !== null);
      chevron.setEnabled(g !== null);
      if (g === null) return;
      shadow.position.set(g.at[0], 0, g.at[1]);
      shadow.scaling.set(g.half[0], 1, g.half[1]);
      const k = Math.min(3, Math.max(0.6, 2 * Math.max(g.half[0], g.half[1])));
      chevron.scaling.set(k, 1, k);
      if (chevron.rotationQuaternion !== null) ringTurn(2, (-g.facingDeg * Math.PI) / 180, chevron.rotationQuaternion);
    },
  };
}
